<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <button
        type="button"
        class="ac-close-button"
        @click="close"
      >
        <img
          src="@shared/assets/img/general/x-black.png"
          alt="x"
          class="ac-close"
        >
      </button>
      <div class="ac-header ml-2">
        Invite a new SmartAC.com member
      </div>
      <div class="flex-1 flex-row justify-end">
        <div class="ac-app-container">
          <img src="@shared/assets/img/logo/white-short.png" alt="SmartAC.com App">
        </div>
      </div>
    </div>
    <div class="ac-body-container">
      <div class="flex-row align-center justify-between margin-bottom-4">
        <div class="sac__font-18 n110 semi-bold">
          Step {{ step }} of 3: {{ headerText }}
        </div>
        <div class="step-container">
          <div class="step-indicator active" />
          <div :class="['step-indicator', step >= 2 ? 'active' : null]" />
          <div :class="['step-indicator', step === 3 ? 'active' : null]" />
        </div>
      </div>
      <div class="sac__font-18 mt-4 mb-4">
        {{ bodyText }}
      </div>
      <ContactInfo
        v-if="step === 1"
        :firstName="firstName"
        :lastName="lastName"
        :email="email"
        :phoneNumber="phoneNumber"
        :corpId="corpId"
        :allPlans="allPlans"
        :providerPlanId="providerPlanId"
        :providerPlans="providerPlans"
        :change="change"
        :errorObj="errorObj"
      />
      <AddressInfo
        v-else-if="step === 2"
        :billingAddress="billingAddress"
        :serviceAddress="serviceAddress"
        :sameAsBilling="sameAsBilling"
        :errorObj="errorObj"
        :change="change"
        :changeAddress="changeAddress"
      />
      <ConfirmInfo
        v-else-if="step === 3"
        :firstName="firstName"
        :lastName="lastName"
        :email="email"
        :phoneNumber="phoneNumber"
        :corpId="corpId"
        :billingAddress="billingAddress"
        :serviceAddress="serviceAddress"
        :providerPlanId="providerPlanId"
        :providerPlans="providerPlans"
        :sameAsBilling="sameAsBilling"
      />
    </div>
    <div class="ac-footer-container">
      <button
        v-if="step > 1"
        type="button"
        class="ac-form-button"
        @click="step -= 1"
      >
        Back
      </button>
      <button
        type="button"
        class="ac-form-button"
        @click="nextStep"
      >
        {{ step === 3 ? 'Send' : 'Next' }}
      </button>
    </div>
    <ProcessOverlay
      :show="processing"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import ProcessOverlay from '@shared/components/ProcessOverlay.vue';

import apiClient from '@shared/lib/general/apiClient';
import formatPhoneNumber from '@shared/lib/formatter/formatPhoneNumber';
import phoneValidation from '@shared/lib/errorCatch/phoneValidation';
import ContactInfo from './ContactInfo.vue';
import AddressInfo from './AddressInfo.vue';
import ConfirmInfo from './ConfirmInfo.vue';

export default {
  name: 'InviteMember',
  components: {
    ProcessOverlay,
    ContactInfo,
    AddressInfo,
    ConfirmInfo,
  },
  data() {
    return {
      step: 1,
      billingAddress: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      },
      serviceAddress: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      },
      sameAsBilling: true,
      corpId: this.corporationId,
      providerPlanId: null,
      providerPlans: [],
      allPlans: [],
      errorObj: {},
      processing: false,
    };
  },
  computed: {
    ...mapState({
      firstName: (state) => state.customer.inviteCustomer.firstName,
      lastName: (state) => state.customer.inviteCustomer.lastName,
      email: (state) => state.customer.inviteCustomer.email,
      phone: (state) => state.customer.inviteCustomer.phone,
      corporationId: (state) => state.admin.adminFilter.corpId,
      allPartners: (state) => state.partner?.partners,
      states: (state) => state.general.states,
      countries: (state) => state.general.countries,
    }),
    ...mapGetters([
      'isSmacAdmin',
      'allBranches',
    ]),
    phoneNumber() {
      return formatPhoneNumber(this.phone);
    },
    headerText() {
      if (this.step === 2) {
        return 'Member Addresses';
      }
      if (this.step === 3) {
        return 'Confirm and send';
      }
      return 'Contact information';
    },
    bodyText() {
      if (this.step === 2) {
        return 'Enter the member\'s service address. Uncheck the box after entering the service address to add a different billing address.';
      }
      if (this.step === 3) {
        return 'Confirm the information below before sending the invitation.';
      }
      return 'Fill out information for individual who will receive text invitation.';
    },
  },
  watch: {
    corpId(corpId) {
      this.getMaintenancePlans();
      this.providerPlanId = null;
      const partner = this.allPartners.find((p) => p.id === corpId);
      if (partner?.country) {
        this.serviceAddress = {
          ...this.serviceAddress,
          country: partner.country,
        };
        this.billingAddress = {
          ...this.billingAddress,
          country: partner.country,
        };
      }
    },
  },
  async mounted() {
    if (this.isSmacAdmin) {
      await this.$store.dispatch('getAllPartners');
    }
    this.corpId = this.corporationId;
    this.getMaintenancePlans();
  },
  methods: {
    close() {
      this.$store.dispatch('changeCustomerValues', [{
        field: 'showActionModal',
        value: false,
      }]);
    },
    change(field, value) {
      if (field === 'firstName' || field === 'lastName' || field === 'email' || field === 'phone') {
        this.$store.dispatch('changeCustomerValue', {
          field: 'inviteCustomer', 
          value: {
            ...this.$store.state.customer.inviteCustomer,
            [field]: value,
          },
        });
      } else {
        this[field] = value;
      }
    },
    changeAddress(type, field, value) {
      if (field === 'address') {
        this[type] = {
          ...this[type],
          ...value,
        };
      } else {
        this[type] = {
          ...this[type],
          [field]: value,
        };
      }
    },
    nextStep() {
      if (this.step === 3) {
        this.inviteMember();
      } else {
        const errorObj = {};
        if (this.step === 1) {
          const {
            firstName,
            lastName,
            email,
            phoneNumber,
            providerPlanId,
          } = this;
          const tFirstName = firstName.trim();
          const tLastName = lastName.trim();
          const tEmail = email.trim();
          if (!tFirstName) {
            errorObj.firstNameErr = 'First name is required.';
          }
          if (!tLastName) {
            errorObj.lastNameErr = 'Last name is required.';
          }
          if (!tEmail) {
            errorObj.emailErr = 'Email is required.';
          }
          if (!phoneNumber) {
            errorObj.phoneErr = 'Phone is required.';
          } else if (!phoneValidation(phoneNumber)) {
            errorObj.phoneErr = 'Phone is not valid.';
          }
          if (!providerPlanId) {
            errorObj.providerPlanErr = 'SmartMaintenance Plan is required.';
          }
        } else if (this.step === 2) {
          const {
            billingAddress,
            serviceAddress,
            sameAsBilling,
          } = this;
          const bAddress1 = billingAddress.address1.trim();
          const bCity = billingAddress.city.trim();
          const bState = billingAddress.state.trim();
          const bZip = billingAddress.zip.trim();
          const bCountry = billingAddress.country.trim();
          const sAddress1 = serviceAddress.address1.trim();
          const sCity = serviceAddress.city.trim();
          const sState = serviceAddress.state.trim();
          const sZip = serviceAddress.zip.trim();
          const sCountry = serviceAddress.country.trim();
          if (!sAddress1) {
            errorObj.sAddress1Err = 'Address is required.';
          }
          if (!sCity) {
            errorObj.sCityErr = 'City is required.';
          }
          if (!sState) {
            errorObj.sStateErr = 'State is required.';
          }
          if (!sZip) {
            errorObj.sZipErr = 'Zip code is required.';
          }
          if (!sCountry) {
            errorObj.sCountryErr = 'Country is required.';
          }
          if (!sameAsBilling) {
            if (!bAddress1) {
              errorObj.address1Err = 'Address is required.';
            }
            if (!bCity) {
              errorObj.cityErr = 'City is required.';
            }
            if (!bState) {
              errorObj.stateErr = 'State is required.';
            }
            if (!bZip) {
              errorObj.zipErr = 'Zip code is required.';
            }
            if (!bCountry) {
              errorObj.countryErr = 'Country is required.';
            }
          }
        }
        this.errorObj = errorObj;
        if (Object.keys(errorObj).length === 0) {
          this.step += 1;
        }
      }
    },
    async getMaintenancePlans() {
      const {
        corpId,
      } = this;
      const { data } = await apiClient({
        method: 'GET',
        url: `/provider/plans/corp/${corpId}/all`,
      });
      const activeAppOnlyPlans = data.filter(
        (p) => p.planType === 'AppOnly' && p.status === 'Active',
      );
      this.allPlans = activeAppOnlyPlans;
      this.providerPlans = activeAppOnlyPlans.map((plan) => ({
        label: plan.name,
        value: plan.planId,
      }));
      this.providerPlanId = this.providerPlans.length >= 1 ? this.providerPlans[0].value : null;
    },
    async inviteMember() {
      const {
        corpId,
        firstName,
        lastName,
        email,
        phoneNumber,
        billingAddress,
        serviceAddress,
        sameAsBilling,
        providerPlanId,
      } = this;
      const tFirstName = firstName.trim();
      const tLastName = lastName.trim();
      const tEmail = email.trim();
      const bAddress1 = billingAddress.address1.trim();
      const bAddress2 = billingAddress.address2.trim();
      const bCity = billingAddress.city.trim();
      const bState = billingAddress.state.trim();
      const bZip = billingAddress.zip.trim();
      const bCountry = billingAddress.country.trim();
      const sAddress1 = serviceAddress.address1.trim();
      const sAddress2 = serviceAddress.address2.trim();
      const sCity = serviceAddress.city.trim();
      const sState = serviceAddress.state.trim();
      const sZip = serviceAddress.zip.trim();
      const sCountry = serviceAddress.country.trim();

      this.processing = true;
      const sAddress = {
        address1: sAddress1,
        address2: sAddress2,
        city: sCity,
        state: sState,
        zip: sZip,
        country: sCountry,
      };
      let bAddress = sAddress;
      if (!sameAsBilling) {
        bAddress = {
          address1: bAddress1,
          address2: bAddress2,
          city: bCity,
          state: bState,
          zip: bZip,
          country: bCountry,
        };
      }
      try {
        await apiClient({
          method: 'POST',
          url: '/customers/invite',
          data: {
            firstName: tFirstName,
            lastName: tLastName,
            email: tEmail,
            phone: phoneNumber,
            corporationId: corpId,
            billingAddress: bAddress,
            serviceAddress: [sAddress],
            providerPlanId,
          },
        });
        this.$store.dispatch('showSuccessToast', {
          message: `Your invitation was sent to ${firstName} ${lastName}.`,
        });
        this.$store.dispatch('getAllCustomers');
        this.$store.dispatch('changeCustomerValue', {
          field: 'inviteCustomer', 
          value: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
          },
        });
        this.close();
      } catch (err) {
        const error = {};
        if (err?.response?.data?.message?.includes('exists')) {
          error.emailErr = err.response.data.message;
          this.errorObj = error;
          this.step = 1;
        }
        console.error(err);
      }
      this.processing = false;
    },
  },
};
</script>

<style lang="scss">
.ac-app-container {
  width: 54px;
  height: 54px;
  background-color: $N100;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 80%;
  }
}

.step-container {
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  .step-indicator {
    width: 82px;
    height: 16px;
    background-color: $N20;
    &.active {
      background-color: $NBLUE100;
    }
    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }
    &:nth-child(2) {
      margin-left: 4px;
      margin-right: 4px;
    }
    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }
}
</style>
